import { v4 as uuidv4 } from "uuid";
import { Message, MessageContext } from "@/store/zustand/supabaseStore";
import { fromClaudeCodeUserMessage } from "@/components/AI/chat/utils/messages";

// Claude Code API Types - Using discriminated unions for type safety
type ClaudeMessageContent =
  | {
      type: "text";
      text: string;
    }
  | {
      type: "image";
      source:
        | {
            type: "base64";
            media_type: "image/jpeg" | "image/png" | "image/gif" | "image/webp";
            data: string;
          }
        | {
            type: "url";
            url: string;
          };
    }
  | {
      type: "tool_use";
      id: string;
      name: string;
      input: Record<string, unknown>;
    }
  | {
      type: "tool_result";
      tool_use_id: string;
      content: string;
      is_error: boolean;
    };

// Type aliases for cleaner function signatures
type ToolUseContentBlock = Extract<ClaudeMessageContent, { type: "tool_use" }>;
type ToolResultContentBlock = Extract<
  ClaudeMessageContent,
  { type: "tool_result" }
>;

// Only maps a subset of the toolUseResult fields that are used in the UI
interface ToolUseResult {
  filePath?: string;
  originalFile?: string;
  originalFileContents?: string;
  type?: string;
  activityId?: string;
}

interface ClaudeMessage {
  type: "user" | "assistant";
  message?: {
    id?: string;
    content: string | ClaudeMessageContent[];
  };
  uuid?: string;
  timestamp?: string;
  toolUseResult?: ToolUseResult;
}

interface TransformStep {
  text?: string;
  toolCall?: {
    toolCallId: string;
    name: string;
    argumentsParsed: Record<string, unknown>;
  };
  toolResult?: string;
}

interface ContentBlock {
  type: "text" | "image" | "tool-call" | "tool-result";
  text?: string;
  toolName?: string;
  toolCallId?: string;
  args?: Record<string, unknown>;
  status?: "in-progress" | "succeeded" | "failed" | "aborted";
  content?: string;
  source?: any; // For image blocks - Claude's ImageBlockParam.Source
}

/**
 * Construct messages for Claude Code using the same logic as constructMessages
 * This replicates the logic from chatWithAI.ts constructMessages function
 */
const constructMessagesForClaudeCode = (
  usedIds: { [key: number]: string },
  createdAt: { [key: string]: Date },
  steps: TransformStep[],
  conversationId: string,
  canvasId: string,
  getId: () => string,
  originalTimestamp?: string,
): Message[] => {
  const newMessages: Message[] = [];

  // Track tool calls and their results to determine status
  const toolCallStatuses: {
    [toolCallId: string]: "in-progress" | "succeeded" | "failed" | "aborted";
  } = {};

  steps.forEach((step) => {
    if (step.toolResult && step.toolCall) {
      try {
        const parsedResult = JSON.parse(step.toolResult);
        // Check for explicit status field or assume success if result exists
        if (parsedResult && typeof parsedResult === "object") {
          if (parsedResult.status) {
            toolCallStatuses[step.toolCall.toolCallId] =
              parsedResult.status === "success" ? "succeeded" : "failed";
          } else {
            // If we have a valid result object but no status, assume succeeded
            toolCallStatuses[step.toolCall.toolCallId] = "succeeded";
          }
        } else {
          // If result is not an object, assume succeeded
          toolCallStatuses[step.toolCall.toolCallId] = "succeeded";
        }
      } catch {
        // If we can't parse the result, assume succeeded
        toolCallStatuses[step.toolCall.toolCallId] = "succeeded";
      }
    }
  });

  // Combine all content into a single message while preserving sequence
  const combinedContent: ContentBlock[] = [];

  steps.forEach((step) => {
    // Add text content in sequence
    if (step.text) {
      combinedContent.push({
        type: "text",
        text: step.text,
      });
    }

    // Add tool calls in sequence
    if (step.toolCall) {
      try {
        const status =
          toolCallStatuses[step.toolCall.toolCallId] || "in-progress";

        // Defensive checks for required fields
        const toolCallId = step.toolCall.toolCallId || "";
        const toolName = step.toolCall.name || "unknown";
        const args = step.toolCall.argumentsParsed;

        // Ensure args is a valid object
        let safeArgs: Record<string, unknown> = {};
        if (args && typeof args === "object" && !Array.isArray(args)) {
          safeArgs = args;
        } else {
          console.warn(
            `Invalid argumentsParsed for tool call ${toolCallId}:`,
            args,
          );
        }

        combinedContent.push({
          type: "tool-call",
          args: safeArgs,
          toolName: toolName,
          toolCallId: toolCallId,
          status: status,
        });
      } catch (error) {
        console.error("Error processing tool call step:", error, step.toolCall);
        // Continue processing other steps even if this one fails
      }

      // Add tool results in sequence for valid tool names
      if (step.toolResult) {
        combinedContent.push({
          type: "tool-result",
          content: step.toolResult,
          toolCallId: step.toolCall.toolCallId,
        });
      }
    }
  });

  // Create a single message with all content if we have any content
  if (combinedContent.length > 0) {
    const messageId = getId();
    const message: Message = {
      id: messageId,
      role: "assistant",
      content: JSON.stringify(combinedContent),
      conversation_id: conversationId,
      canvas_id: canvasId,
      message_context: undefined,
      tool_calls: null,
      tool_call_id: null,
      created_at: originalTimestamp || new Date().toISOString(),
      auth0_user_id_sent: null,
      chat_only_mode: false,
    };

    newMessages.push(message);
  }

  return newMessages;
};

/**
 * Transform Claude Code tool calls to standard format
 */
export const transformToolCall = (
  messageContentBlock: ToolUseContentBlock,
): ContentBlock => {
  // messageContentBlock is from message.content array: { type: "tool_use", id, name, input }

  // Defensive checks for malformed message content block
  if (!messageContentBlock || typeof messageContentBlock !== "object") {
    console.error("Invalid messageContentBlock:", messageContentBlock);
    return {
      type: "tool-call",
      toolName: "unknown",
      toolCallId: "",
      args: {},
      status: "failed",
    };
  }

  // Map Claude Code tool names to standard tool names for consistent processing
  const toolNameMap: Record<string, string> = {
    Glob: "globTool",
    Grep: "grepTool",
    Read: "readFilesTool",
    Bash: "runTerminalCommandTool",
    Edit: "editTool",
    Write: "editTool",
    MultiEdit: "editTool",
    Task: "taskTool",
    TodoWrite: "todoWriteTool",
    WebSearch: "webSearchTool",
    "mcp__tempo-mcp__createStoryBoardTool": "createStoryBoardTool",
    "mcp__tempo-mcp__storySpecGeneratorTool": "storySpecGeneratorTool",
    "mcp__tempo-mcp__supabaseDeployEdgeFunctionTool":
      "supabaseDeployEdgeFunctionTool",
    "mcp__tempo-mcp__supabaseRunMigrationTool": "supabaseRunMigrationTool",
    "mcp__tempo-mcp__restartDevServerTool": "restartDevServerTool",
  };

  // Map Claude Code arguments to standard format based on tool type
  const transformArgs = (
    toolName: string,
    claudeArgs: Record<string, unknown>,
  ): Record<string, unknown> => {
    // Defensive check for malformed arguments
    if (!claudeArgs || typeof claudeArgs !== "object") {
      console.warn(`Invalid arguments for tool ${toolName}:`, claudeArgs);
      return {};
    }

    try {
      switch (toolName) {
        case "Glob":
          return {
            globPatterns: [claudeArgs?.pattern || ""],
          };
        case "Grep":
          return {
            grepSearchPatterns: [claudeArgs?.pattern || ""],
            path: claudeArgs?.path,
          };
        case "Read":
          return {
            filenames: [claudeArgs?.file_path || ""],
          };
        case "Bash":
          return {
            command: claudeArgs?.command || "",
          };
        case "Edit":
          return {
            file_path: claudeArgs?.file_path,
            old_string: claudeArgs?.old_string,
            new_string: claudeArgs?.new_string,
          };
        case "Write":
        case "MultiEdit":
          return {
            file_path: claudeArgs?.file_path,
            old_string: claudeArgs?.old_string,
            new_string: claudeArgs?.new_string,
            content: claudeArgs?.content,
            edits: claudeArgs?.edits,
          };
        case "Task":
          return {
            subagent_type: claudeArgs?.subagent_type,
            description: claudeArgs?.description,
            prompt: claudeArgs?.prompt,
          };
        case "TodoWrite":
          return {
            todos: claudeArgs?.todos || [],
          };
        case "WebSearch":
          return {
            query: claudeArgs?.query || "",
            allowed_domains: claudeArgs?.allowed_domains,
            blocked_domains: claudeArgs?.blocked_domains,
          };
        default:
          // For unknown tools, pass through the original args safely
          return claudeArgs || {};
      }
    } catch (error) {
      console.error(
        `Error transforming arguments for tool ${toolName}:`,
        error,
        claudeArgs,
      );
      return {};
    }
  };

  try {
    const toolName = messageContentBlock.name || "unknown";
    const toolCallId = messageContentBlock.id || "";
    const toolInput = messageContentBlock.input || {};

    const standardToolName = toolNameMap[toolName] || toolName;
    const standardArgs = transformArgs(toolName, toolInput);

    return {
      type: "tool-call",
      toolName: standardToolName,
      toolCallId: toolCallId,
      args: standardArgs,
      // Status will be determined by parseMessageContentBlocks from tool-result blocks
    };
  } catch (error) {
    console.error("Error in transformToolCall:", error, messageContentBlock);
    return {
      type: "tool-call",
      toolName: "unknown",
      toolCallId: "",
      args: {},
      // Status will be determined by parseMessageContentBlocks from tool-result blocks
    };
  }
};

/**
 * Transform Claude Code tool result to standard format
 */
export const transformToolResult = (
  messageContentBlock: ToolResultContentBlock,
  toolUseResultMetadata?: ToolUseResult,
): ContentBlock => {
  // messageContentBlock is from message.content array: { tool_use_id, type, content, is_error }
  // toolUseResultMetadata is from message.toolUseResult field (file path, original contents, etc.)
  // Determine status based on is_error field at the content block level
  const status = messageContentBlock.is_error ? "failed" : "success";

  // Check if this is a Claude Code file editing tool (Edit/MultiEdit/Write) that needs
  // special handling for file path and original contents injection
  // Note: toolUseResultMetadata comes from the message level, not the content block level
  // The Edit tool uses originalFile whereas MultiEdit uses originalFileContents
  const toolUseResult = toolUseResultMetadata;
  const isFileEditingTool =
    toolUseResult &&
    toolUseResult.filePath &&
    (toolUseResult.originalFile ||
      toolUseResult.originalFileContents ||
      toolUseResult.type === "create");

  let wrappedContent;
  if (isFileEditingTool) {
    // Normalize file path by removing /app/ prefix if present
    const normalizedFilePath = toolUseResult.filePath?.startsWith("/app/")
      ? toolUseResult.filePath.replace("/app/", "")
      : toolUseResult.filePath;

    wrappedContent = {
      status: status,
      result: messageContentBlock.content || "",
      filePath: normalizedFilePath,
      activityId: toolUseResult.activityId,
      // Store the toolUseResult for ClaudeCodeEditToolBlock to use (with normalized path)
      toolUseResult: {
        ...toolUseResult,
        filePath: normalizedFilePath,
      },
      // For errors, also include the message field for compatibility
      ...(messageContentBlock.is_error && {
        message: messageContentBlock.content,
      }),
    };
  } else {
    // Regular tool result format
    wrappedContent = {
      status: status,
      result: messageContentBlock.content || "",
      // For errors, also include the message field for compatibility
      ...(messageContentBlock.is_error && {
        message: messageContentBlock.content,
      }),
    };
  }

  return {
    type: "tool-result",
    toolCallId: messageContentBlock.tool_use_id,
    content: JSON.stringify(wrappedContent), // Stringify for parsing compatibility
  };
};

/**
 * Transform Claude Code message content to standard content format
 */
export const transformMessageContent = (
  messageContent: ClaudeMessageContent | ClaudeMessageContent[],
  toolUseResultMetadata?: ToolUseResult,
): ContentBlock[] => {
  const standardContent: ContentBlock[] = [];

  // Handle case where claudeContent might not be an array
  if (!messageContent) {
    return standardContent;
  }

  // Convert to array if it's not already
  const contentArray = Array.isArray(messageContent)
    ? messageContent
    : [messageContent];

  contentArray.forEach((block) => {
    switch (block.type) {
      case "text":
        standardContent.push({
          type: "text",
          text: block.text,
        });
        break;
      case "image":
        // Handle image blocks - convert to standard format for UI display
        standardContent.push({
          type: "image",
          source: block.source,
        });
        break;
      case "tool_use":
        standardContent.push(transformToolCall(block));
        break;
      case "tool_result":
        standardContent.push(transformToolResult(block, toolUseResultMetadata));
        break;
      default:
        // For unknown block types, convert to text block
        standardContent.push({
          type: "text",
          text: JSON.stringify(block),
        });
    }
  });

  return standardContent;
};

/**
 * Transform Claude Code streaming data to standard Message format
 * This is the main function that converts Claude Code messages to be compatible
 * with existing parsing logic
 */
export const transformClaudeCodeMessage = (
  claudeData: ClaudeMessage,
  conversationId: string,
  canvasId: string,
  existingMessage?: Message,
): Message | null => {
  // Handle user messages (including tool results)
  if (claudeData.type === "user" && claudeData.message) {
    // Handle both string content (simple messages) and array content (with tool results)
    let standardContent: ContentBlock[] = [];
    let messageContext: Partial<MessageContext> | undefined;

    if (typeof claudeData.message.content === "string") {
      // Simple text message
      standardContent = [{ type: "text", text: claudeData.message.content }];
    } else if (Array.isArray(claudeData.message.content)) {
      // Complex message with potential tool calls/results - extract context and use cleaned content
      const { context, cleanedContent } = fromClaudeCodeUserMessage(
        claudeData.message.content,
      );

      // Set message context if any was extracted
      if (Object.keys(context).length > 0) {
        messageContext = context;
      }

      // Transform the cleaned content (without context blocks)
      standardContent = transformMessageContent(
        cleanedContent,
        claudeData.toolUseResult,
      );
    } else {
      standardContent = transformMessageContent(
        claudeData.message.content || [],
        claudeData.toolUseResult,
      );
    }

    // Check if this is a tool result message (should be filtered from display)
    const hasToolResult = standardContent.some(
      (block) => block.type === "tool-result",
    );

    const message: Message = {
      id: claudeData.uuid || `user-${Date.now()}`,
      conversation_id: conversationId,
      canvas_id: canvasId,
      // Store as JSON string for compatibility with parseMessageContentBlocks
      content: JSON.stringify(standardContent),
      role: "user" as const,
      message_context: messageContext,
      tool_calls: null,
      tool_call_id: hasToolResult
        ? standardContent.find((b) => b.type === "tool-result")?.toolCallId ||
          null
        : null,
      created_at: claudeData.timestamp || new Date().toISOString(),
      auth0_user_id_sent: null,
      chat_only_mode: false,
    };

    return message;
  }

  // Handle assistant messages
  if (claudeData.type === "assistant") {
    // Extract Claude message ID for grouping
    const claudeMessageId = claudeData.message?.id;
    // For streaming, we need to accumulate content from existing message
    let accumulatedContent: ContentBlock[] = [];

    // Parse existing content if updating an existing message
    if (existingMessage?.content) {
      try {
        if (
          typeof existingMessage.content === "string" &&
          existingMessage.content.startsWith("[")
        ) {
          accumulatedContent = JSON.parse(existingMessage.content);
        } else if (Array.isArray(existingMessage.content)) {
          accumulatedContent = existingMessage.content as ContentBlock[];
        } else if (typeof existingMessage.content === "string") {
          accumulatedContent = [
            { type: "text", text: existingMessage.content },
          ];
        }
      } catch (e) {
        // If parsing fails, start fresh
        accumulatedContent = [];
      }
    }

    // Transform new content from this chunk
    if (claudeData.message?.content) {
      let newContent: ContentBlock[] = [];

      if (typeof claudeData.message.content === "string") {
        // Simple text message
        newContent = [{ type: "text", text: claudeData.message.content }];
      } else if (Array.isArray(claudeData.message.content)) {
        // Complex message with tool calls/results
        newContent = transformMessageContent(
          claudeData.message.content,
          claudeData.toolUseResult,
        );
      } else {
        newContent = transformMessageContent(
          claudeData.message.content || [],
          claudeData.toolUseResult,
        );
      }

      newContent.forEach((newBlock) => {
        if (newBlock.type === "text") {
          // For text blocks, check if we can append to the LAST text block to maintain sequence
          const lastBlock = accumulatedContent[accumulatedContent.length - 1];
          if (lastBlock && lastBlock.type === "text") {
            // Only append if the new text isn't already included
            const lastText = lastBlock.text || "";
            const newText = newBlock.text || "";
            if (!lastText.includes(newText)) {
              lastBlock.text = lastText + newText;
            }
          } else {
            // Create new text block to preserve sequence
            accumulatedContent.push(newBlock);
          }
        } else if (newBlock.type === "tool-call") {
          // Add tool calls that aren't already present
          const existingToolCall = accumulatedContent.find(
            (block) =>
              block.type === "tool-call" &&
              block.toolCallId === newBlock.toolCallId,
          );
          if (!existingToolCall) {
            accumulatedContent.push(newBlock);
          }
        } else {
          // For other block types (tool-result, etc.), add if not already present
          const blockKey =
            newBlock.type === "tool-result"
              ? newBlock.toolCallId || newBlock.type
              : newBlock.type;
          const existingBlock = accumulatedContent.find((block) => {
            if (
              newBlock.type === "tool-result" &&
              block.type === "tool-result"
            ) {
              return block.toolCallId === newBlock.toolCallId;
            }
            return block.type === newBlock.type;
          });
          if (!existingBlock) {
            accumulatedContent.push(newBlock);
          }
        }
      });
    }

    // Only create message if we have content
    if (accumulatedContent.length > 0 || existingMessage) {
      // Use Claude message ID as the base for Tempo message ID to enable grouping
      const messageId =
        existingMessage?.id ||
        (claudeMessageId ? `claude-${claudeMessageId}` : uuidv4());

      // Check if this message contains tool calls to set tool_call_id appropriately
      const toolCallBlocks = accumulatedContent.filter(
        (block) => block.type === "tool-call",
      );
      const toolCallId =
        toolCallBlocks.length > 0 ? toolCallBlocks[0].toolCallId || null : null;

      const message: Message = {
        id: messageId,
        conversation_id: conversationId,
        canvas_id: canvasId,
        // Store as JSON string for compatibility with parseMessageContentBlocks
        content: JSON.stringify(accumulatedContent),
        role: "assistant" as const,
        message_context: undefined,
        tool_calls: null,
        tool_call_id: toolCallId, // Set to first tool call ID if any tool calls exist
        created_at:
          existingMessage?.created_at ||
          claudeData.timestamp ||
          new Date().toISOString(),
        auth0_user_id_sent: null,
        chat_only_mode: false,
      };

      return message;
    }
  }

  return null;
};

/**
 * Preprocesses Claude messages to convert user messages with tool results to assistant type
 * This ensures consistent handling between streaming and non-streaming modes
 */
const preprocessClaudeMessage = (claudeMsg: ClaudeMessage): ClaudeMessage => {
  if (claudeMsg.type === "user" && claudeMsg.message?.content) {
    // Check if this user message contains tool results
    const content = Array.isArray(claudeMsg.message.content)
      ? claudeMsg.message.content
      : [claudeMsg.message.content];

    const hasToolResult = content.some(
      (block) =>
        block && typeof block === "object" && block.type === "tool_result",
    );

    if (hasToolResult) {
      // Transform to assistant type
      return {
        ...claudeMsg,
        type: "assistant" as const,
      };
    }
  }
  return claudeMsg;
};

/**
 * Transform Claude Code API response (array of messages) to standard Message format
 * Uses role boundaries to group messages and constructMessages logic
 */
export const transformClaudeCodeApiResponse = (
  claudeMessages: ClaudeMessage[],
  conversationId: string,
  canvasId: string,
): Message[] => {
  if (!Array.isArray(claudeMessages)) {
    console.error("claudeMessages is not an array:", claudeMessages);
    return [];
  }

  // Step 1: Transform tool-result user messages to assistant role
  const preprocessedMessages = claudeMessages.map(preprocessClaudeMessage);

  // Step 2: Group messages by type boundaries (user messages as delimiters)
  const messageGroups: ClaudeMessage[][] = [];
  let currentGroup: ClaudeMessage[] = [];

  for (const msg of preprocessedMessages) {
    if (msg.type === "user") {
      // User message starts a new group
      if (currentGroup.length > 0) {
        messageGroups.push(currentGroup);
      }
      currentGroup = [msg];
    } else if (msg.type === "assistant") {
      // Assistant message belongs to current group
      currentGroup.push(msg);
    }
  }

  // Don't forget the last group
  if (currentGroup.length > 0) {
    messageGroups.push(currentGroup);
  }

  // Step 3: Transform each group into Messages
  const finalMessages: Message[] = [];
  let idCounter = 0;
  const usedIds: { [key: number]: string } = {};
  const createdAt: { [key: string]: Date } = {};

  const getId = () => {
    if (!usedIds[idCounter]) {
      usedIds[idCounter] = `claude-group-${Date.now()}-${idCounter}`;
      // Don't set timestamp here - we'll use original timestamps
    }
    idCounter += 1;
    return usedIds[idCounter - 1];
  };

  for (const group of messageGroups) {
    if (group.length === 0) continue;

    // Process each group: user message followed by combined assistant messages
    const userMsg = group.find((msg) => msg.type === "user");
    const assistantMsgs = group.filter((msg) => msg.type === "assistant");

    // Handle user message first
    if (userMsg) {
      const userMessage = transformClaudeCodeMessage(
        userMsg,
        conversationId,
        canvasId,
      );
      if (userMessage) {
        finalMessages.push(userMessage);
      }
    }

    // Handle assistant messages (convert to steps and combine)
    if (assistantMsgs.length > 0) {
      // Convert assistant messages to steps format
      const steps: TransformStep[] = [];
      let earliestTimestamp: string | undefined;

      for (const assistantMsg of assistantMsgs) {
        const transformedMsg = transformClaudeCodeMessage(
          assistantMsg,
          conversationId,
          canvasId,
        );

        // Track the earliest timestamp from the assistant messages
        if (transformedMsg?.created_at) {
          if (
            !earliestTimestamp ||
            transformedMsg.created_at < earliestTimestamp
          ) {
            earliestTimestamp = transformedMsg.created_at;
          }
        }

        if (transformedMsg && transformedMsg.content) {
          try {
            const content =
              typeof transformedMsg.content === "string"
                ? JSON.parse(transformedMsg.content)
                : [];

            // Convert content blocks to steps
            content.forEach((block: ContentBlock) => {
              try {
                if (!block || typeof block !== "object") {
                  console.warn("Invalid content block:", block);
                  return;
                }

                if (block.type === "text") {
                  steps.push({ text: block.text || "" });
                } else if (block.type === "tool-call") {
                  // Defensive handling for args - ensure it's a valid object
                  let safeArgs: Record<string, unknown> = {};
                  if (
                    block.args &&
                    typeof block.args === "object" &&
                    !Array.isArray(block.args)
                  ) {
                    safeArgs = block.args;
                  } else if (block.args) {
                    console.warn(
                      "Invalid args in tool-call block:",
                      block.args,
                    );
                  }

                  steps.push({
                    toolCall: {
                      toolCallId: block.toolCallId || "",
                      name: block.toolName || "",
                      argumentsParsed: safeArgs,
                    },
                  });
                } else if (block.type === "tool-result") {
                  // Find matching tool call step and add result
                  const matchingStepIndex = steps.findIndex(
                    (step) => step.toolCall?.toolCallId === block.toolCallId,
                  );
                  if (matchingStepIndex !== -1) {
                    steps[matchingStepIndex].toolResult = block.content;
                  }
                }
              } catch (blockError) {
                console.error(
                  "Error processing content block:",
                  blockError,
                  block,
                );
                // Continue processing other blocks
              }
            });
          } catch (e) {
            console.error("Error parsing assistant message content:", e);
          }
        }
      }

      // Use constructMessages logic to combine steps
      if (steps.length > 0) {
        const combinedMessages = constructMessagesForClaudeCode(
          usedIds,
          createdAt,
          steps,
          conversationId,
          canvasId,
          getId,
          earliestTimestamp, // Pass the earliest timestamp from the group
        );
        finalMessages.push(...combinedMessages);
      }
    }
  }

  return finalMessages;
};

/**
 * Process streaming chunk and update messages
 */
export const processStreamingChunk = (
  chunk: ClaudeMessage,
  currentMessages: Message[],
  conversationId: string,
  canvasId: string,
): Message[] => {
  if (!currentMessages || currentMessages.length === 0) {
    return currentMessages;
  }

  // Apply preprocessing to the chunk (convert user messages with tool results to assistant type)
  const processedChunk = preprocessClaudeMessage(chunk);

  // Split messages into existing messages (everything except the current streaming assistant message) and current assistant message
  // The current streaming session should only have one assistant message at the end
  let existingMessages: Message[] = [];
  let currentAssistantMessage: Message | undefined;

  // Find where the current streaming assistant message starts
  // It should be after the most recent user message
  let lastUserMessageIndex = -1;
  for (let i = currentMessages.length - 1; i >= 0; i--) {
    if (currentMessages[i].role === "user") {
      lastUserMessageIndex = i;
      break;
    }
  }

  if (lastUserMessageIndex !== -1) {
    // Everything up to and including the last user message is existing
    existingMessages = currentMessages.slice(0, lastUserMessageIndex + 1);

    // If there's an assistant message after the last user message, it's the current streaming one
    if (lastUserMessageIndex + 1 < currentMessages.length) {
      const potentialAssistantMessage =
        currentMessages[lastUserMessageIndex + 1];
      if (potentialAssistantMessage.role === "assistant") {
        currentAssistantMessage = potentialAssistantMessage;
      }
    }
  } else {
    // No user messages found, keep all existing messages
    existingMessages = [...currentMessages];
  }

  // Transform the processed chunk and update the message
  const updatedMessage = transformClaudeCodeMessage(
    processedChunk,
    conversationId,
    canvasId,
    currentAssistantMessage,
  );

  if (updatedMessage && updatedMessage.role === "assistant") {
    // Return existing messages + updated assistant message
    return [...existingMessages, updatedMessage];
  }

  return currentMessages;
};
